import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, X, Check, ArrowRight } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  session_updates: '#00F0FF', messages: '#9D4CDD', open_seat_alerts: '#39FF14',
  tournament_updates: '#FFD700', shop_events: '#F97316', favorite_games: '#EF4444',
  discovery_recommendations: '#3B82F6', rivalry_prompts: '#F59E0B', achievement_updates: '#10B981',
  reminder_alerts: '#F97316', moderation_or_system: '#888', publisher_content: '#3B82F6',
  premium_or_plan: '#9D4CDD', admin_internal: '#EF4444',
  friend_request: '#00F0FF', friend_accepted: '#10B981', group_invite: '#9D4CDD',
  group_joined: '#39FF14', group_message: '#F97316', tournament: '#FFD700',
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

/**
 * NotificationBell — Renders a bell icon with unread badge.
 * Use in nav headers across all portals.
 */
export function NotificationBell({ className = '' }) {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [recent, setRecent] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const dropdownRef = useRef(null);

  const fetchCount = useCallback(() => {
    axios.get(`${API_URL}/api/notifications/unread-count`, { withCredentials: true })
      .then(({ data }) => setCount(data.count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    if (!showDropdown) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  const openDropdown = () => {
    setShowDropdown(true);
    setLoadingRecent(true);
    axios.get(`${API_URL}/api/notifications?limit=6&unread_only=true`, { withCredentials: true })
      .then(({ data }) => setRecent(data))
      .catch(() => {})
      .finally(() => setLoadingRecent(false));
  };

  const markRead = async (id) => {
    await axios.put(`${API_URL}/api/notifications/${id}/read`, {}, { withCredentials: true }).catch(() => {});
    setRecent(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await axios.put(`${API_URL}/api/notifications/read-all`, {}, { withCredentials: true }).catch(() => {});
    setRecent(prev => prev.map(n => ({ ...n, is_read: true })));
    setCount(0);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => showDropdown ? setShowDropdown(false) : openDropdown()}
        className="relative p-2 rounded-xl hover:bg-white/5 transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={22} weight={count > 0 ? 'fill' : 'duotone'} className={count > 0 ? 'text-[#FACC15]' : 'text-[#555]'} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-badge">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      <AnimatePresence>
        {showDropdown && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-12 w-[340px] max-h-[420px] bg-[#0c0c14] border border-white/[0.08] rounded-2xl shadow-2xl overflow-hidden z-50"
            data-testid="notification-dropdown"
          >
            <div className="px-4 py-3 border-b border-white/[0.05] flex items-center justify-between">
              <p className="text-white text-sm font-bold">Notifications</p>
              <div className="flex items-center gap-2">
                {count > 0 && (
                  <button onClick={markAllRead} className="text-[10px] text-[#00F0FF] hover:text-white transition-colors" data-testid="mark-all-read-dropdown">
                    Mark all read
                  </button>
                )}
                <button onClick={() => setShowDropdown(false)} className="text-[#555] hover:text-white"><X size={14} /></button>
              </div>
            </div>

            <div className="overflow-y-auto" style={{ maxHeight: 320 }}>
              {loadingRecent ? (
                <div className="p-6 text-center"><div className="w-5 h-5 border-2 border-[#9D4CDD] border-t-transparent rounded-full animate-spin mx-auto" /></div>
              ) : recent.length === 0 ? (
                <div className="p-6 text-center">
                  <Bell size={28} className="text-[#333] mx-auto mb-2" />
                  <p className="text-[#555] text-xs">All caught up!</p>
                </div>
              ) : (
                recent.map(n => {
                  const cat = n.category || n.notification_type || n.type;
                  const accent = CATEGORY_COLORS[cat] || '#888';
                  return (
                    <div
                      key={n.id}
                      className={`px-4 py-3 flex items-start gap-3 border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors cursor-pointer ${!n.is_read ? 'bg-white/[0.02]' : ''}`}
                      onClick={() => { markRead(n.id); if (n.action_url || n.link) navigate(n.action_url || n.link); }}
                      data-testid={`notification-item-${n.id}`}
                    >
                      <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: n.is_read ? 'transparent' : accent }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-[12px] font-medium truncate">{n.title}</p>
                        <p className="text-[#666] text-[11px] line-clamp-2">{n.body}</p>
                        <span className="text-[10px] text-[#444] mt-0.5 block">{timeAgo(n.created_at)}</span>
                      </div>
                      {!n.is_read && (
                        <button onClick={(e) => { e.stopPropagation(); markRead(n.id); }} className="text-[#444] hover:text-[#10B981] p-1">
                          <Check size={12} />
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <div className="px-4 py-2.5 border-t border-white/[0.05]">
              <button
                onClick={() => { setShowDropdown(false); navigate('/notifications'); }}
                className="w-full text-center text-[11px] text-[#00F0FF] font-medium hover:text-white transition-colors flex items-center justify-center gap-1"
                data-testid="view-all-notifications"
              >
                View all <ArrowRight size={11} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
